import React, { useEffect, useMemo, useState } from 'react';
import { Search, Star, Clock, MapPin, X, ShoppingCart } from 'lucide-react';
import marketApi from './marketApi';
import OptionsPicker from './OptionsPicker';
import { useCart } from './CartContext';
import { ratingText } from './ratingText';

/**
 * Boutique d'un fournisseur — rayons/sous-rayons (onglets latéraux en desktop),
 * recherche interne, fiche produit avec OPTIONS & instructions → panier.
 * Mêmes règles que l'app native (héritage des groupes par rayon/sous-rayon).
 */
export default function StorePage({ supplierId, nav }) {
  const { addToCart, cart } = useCart();
  const [supplier, setSupplier] = useState(null);
  const [activePromos, setActivePromos] = useState([]); // promos boutique (bandeau)
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]); // avis publiés (auteur en prénom + initiale, réponse du commerce)
  const [groups, setGroups] = useState([]); // groupes d'options
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');

  // Fiche produit
  const [sel, setSel] = useState(null);
  const [opts, setOpts] = useState([]);
  const [instr, setInstr] = useState('');
  const [qty, setQty] = useState(1);
  const [confirmSwitch, setConfirmSwitch] = useState(null); // produit en attente si panier d'un autre commerce

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const [det, cat] = await Promise.all([
        marketApi.supplierDetail(supplierId).catch(() => null),
        marketApi.supplierCategories(supplierId).catch(() => null),
      ]);
      if (!alive) return;
      if (det?.success) {
        setSupplier(det.supplier);
        setProducts(det.products || []);
        setReviews(det.reviews || []);
        setGroups(det.option_groups || []);
        setActivePromos(det.active_promos || []);
      }
      setCats(cat?.categories || []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [supplierId]);

  const groupsFor = (p) => groups.filter(g =>
    (g.category_ids || []).some(cid => cid === p?.category_id || cid === p?.subcategory_id)
    && (g.options || []).some(o => o.is_available !== false)
  );

  const q = search.trim().toLowerCase();
  const visible = useMemo(() => {
    let list = products.filter(p => p.is_available !== false);
    if (q) return list.filter(p => (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
    if (activeCat === 'all') return list;
    if (activeCat === 'other') return list.filter(p => !p.category_id);
    return list.filter(p => p.category_id === activeCat);
  }, [products, q, activeCat]);

  // Regroupe par sous-catégorie au sein du rayon actif (séparateurs).
  const activeCatObj = cats.find(c => c.category_id === activeCat);
  const blocks = useMemo(() => {
    if (q || activeCat === 'all' || activeCat === 'other' || !activeCatObj?.subcategories?.length) {
      return [{ sub: null, items: visible }];
    }
    const out = [{ sub: null, items: visible.filter(p => !p.subcategory_id) }];
    for (const s of activeCatObj.subcategories) {
      const items = visible.filter(p => p.subcategory_id === s.category_id);
      if (items.length) out.push({ sub: s.name, items });
    }
    return out.filter(b => b.items.length);
  }, [visible, q, activeCat, activeCatObj]);

  const openProduct = (p) => { setSel(p); setOpts([]); setInstr(''); setQty(1); };

  const doAdd = (p, options, instructions, quantity) => {
    addToCart(supplierId, supplier?.business_name, {
      product_id: p.product_id, name: p.name, price: p.price, promo_price: p.promo_price,
      bogo: p.bogo || null, // règle « X achetés = Y offert » (le serveur re-tarife)
      rx_required: p.rx_required === true, rx_original_return: p.rx_original_return === true,
      max_qty_per_order: p.max_qty_per_order || null,
      image: p.image_thumb || p.image_url || p.image, unit: p.unit,
      options, special_instructions: instructions, quantity,
    });
    setSel(null);
  };

  const tryAdd = (p, options = [], instructions = '', quantity = 1) => {
    if (cart && cart.supplierId !== supplierId) {
      setConfirmSwitch({ p, options, instructions, quantity });
      return;
    }
    doAdd(p, options, instructions, quantity);
  };

  const quickAdd = (p) => {
    if (groupsFor(p).length > 0) openProduct(p);
    else tryAdd(p);
  };

  if (loading) return <div className="mk-center"><div className="mk-spin" /> Chargement de la boutique…</div>;
  if (!supplier) return <div className="mk-center">Boutique introuvable.</div>;

  const unitTotal = sel ? ((sel.promo_price || sel.price || 0) + opts.reduce((s, o) => s + (o.price_delta || 0), 0)) : 0;

  return (
    <div className="mk-container">
      {/* En-tête boutique : photo de couverture en bannière (si le commerce en a une), logo qui la chevauche */}
      {supplier.cover_image ? (
        <div className="mk-store-cover"><img src={supplier.cover_image} alt="" /></div>
      ) : null}
      <div className={`mk-store-head${supplier.cover_image ? ' with-cover' : ''}`}>
        {supplier.logo
          ? <img className="mk-store-logo" src={supplier.logo_thumb || supplier.logo} alt="" />
          : <div className="mk-store-logo mk-card-ph">🏪</div>}
        <div className="mk-store-info">
          <h2>{supplier.business_name}</h2>
          <div className="mk-meta">
            <Star size={13} fill="#FFB900" color="#FFB900" /> {ratingText(supplier.rating, supplier.rating_count)}
            {supplier.preparation_time ? <> · <Clock size={13} /> ~{supplier.preparation_time} min</> : null}
            {supplier.address ? <> · <MapPin size={13} /> {supplier.address}</> : null}
          </div>
          <span className={`mk-open ${supplier.is_open ? 'on' : 'off'}`} style={{ marginTop: 6, display: 'inline-block' }}>
            {supplier.is_open ? 'Ouvert' : 'Fermé'}
          </span>
        </div>
      </div>

      {/* Avis clients (lecture) */}
      {reviews.length > 0 && (
        <section className="mk-section" style={{ padding: '0 16px 8px' }}>
          <h3 style={{ margin: '12px 0 8px', fontSize: '1.05rem' }}>Avis clients <span style={{ color: '#9AA0A8', fontWeight: 500, fontSize: '0.85rem' }}>· {supplier.rating_count || reviews.length} avis</span></h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {reviews.map(r => (
              <div key={r.review_id} style={{ background: '#fff', borderRadius: 12, padding: '10px 12px', border: '1px solid #E9EBEF' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: '0.9rem' }}>{r.reviewer_name}</strong>
                  <span style={{ color: '#FFB900', letterSpacing: 1 }}>{'★'.repeat(r.rating)}<span style={{ color: '#DCE0E6' }}>{'★'.repeat(5 - r.rating)}</span></span>
                </div>
                {r.comment && <p style={{ margin: '6px 0 0', fontSize: '0.88rem', color: '#414852' }}>{r.comment}</p>}
                {r.response?.text && <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#166534', borderLeft: '3px solid #F54119', paddingLeft: 8 }}>Réponse du commerce : {r.response.text}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pharmacie : garde + devis sur ordonnance */}
      {supplier.business_type === 'pharmacy' && (
        <div className="mk-pharmabox">
          {supplier.on_duty && <span className="mk-onduty">🌙 Pharmacie de garde</span>}
          <div>Vous ne savez pas quoi choisir ? Envoyez la photo de votre ordonnance : la pharmacie vous prépare un devis.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button className="mk-btn primary" onClick={() => nav({ page: 'rx-quote', supplierId, supplierName: supplier.business_name })}>📷 Envoyer mon ordonnance</button>
            <button className="mk-btn ghost" onClick={() => nav({ page: 'my-quotes' })}>Mes devis</button>
          </div>
        </div>
      )}

      {/* Bandeau promotions boutique */}
      {activePromos.length > 0 && (
        <div className="mk-promobanner">
          {activePromos.slice(0, 3).map(pr => (
            <div key={pr.promo_id}>
              {pr.type === 'percent'
                ? `🔥 -${pr.percent}% ${pr.category_id ? 'sur une sélection' : 'sur toute la boutique'}`
                : pr.type === 'bogo'
                  ? `🎁 ${pr.buy_qty} acheté${pr.buy_qty > 1 ? 's' : ''} = ${pr.free_qty} offert${pr.free_qty > 1 ? 's' : ''}`
                  : `💸 -${(pr.discount_amount || 0).toLocaleString('fr-FR')} FC dès ${(pr.min_amount || 0).toLocaleString('fr-FR')} FC d'achat`}
              {pr.ends_at ? ` · jusqu'au ${new Date(pr.ends_at).toLocaleDateString('fr-FR')}` : ''}
            </div>
          ))}
        </div>
      )}

      {/* Recherche interne */}
      <div className="mk-search" style={{ marginTop: 8 }}>
        <Search size={18} color="var(--mb-text-3)" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher dans la boutique…" />
        {search && <button className="mk-iconbtn" style={{ width: 30, height: 30 }} onClick={() => setSearch('')}><X size={15} /></button>}
      </div>

      <div className="mk-store-layout">
        {/* Rayons */}
        {cats.length > 0 && !q && (
          <div className="mk-raytabs">
            <button className={`mk-raytab${activeCat === 'all' ? ' active' : ''}`} onClick={() => setActiveCat('all')}>Tout</button>
            {cats.map(c => (
              <button key={c.category_id} className={`mk-raytab${activeCat === c.category_id ? ' active' : ''}`} onClick={() => setActiveCat(c.category_id)}>
                {c.icon ? `${c.icon} ` : ''}{c.name}{c.product_count ? ` (${c.product_count})` : ''}
              </button>
            ))}
            {products.some(p => !p.category_id) && (
              <button className={`mk-raytab${activeCat === 'other' ? ' active' : ''}`} onClick={() => setActiveCat('other')}>📦 Autres</button>
            )}
          </div>
        )}

        {/* Produits */}
        <div>
          {blocks.map((b, bi) => (
            <div className="mk-prodgrid" key={bi} style={{ marginTop: bi ? 14 : 0 }}>
              {b.sub && <div className="mk-subhead">↳ {b.sub}</div>}
              {b.items.map(p => (
                <div className="mk-card" key={p.product_id} onClick={() => openProduct(p)} role="button" tabIndex={0}>
                  <div className="mk-card-imgwrap">
                    {(p.image_url || p.image)
                      ? <img className="mk-card-img" src={p.image_thumb || p.image_url || p.image} alt="" loading="lazy" />
                      : <div className="mk-card-img mk-card-ph">🍽️</div>}
                  </div>
                  <span className="mk-card-title">{p.name}{p.bogo ? <span className="mk-bogoflag">🎁 {p.bogo.buy_qty}+{p.bogo.free_qty}</span> : null}</span>
                  {p.rx_required && <span className="mk-rxflag">📋 Sur ordonnance{p.rx_original_return ? ' · original à remettre' : ''}</span>}
                  {p.description && <span className="mk-meta" style={{ display: 'block' }}>{p.description.slice(0, 60)}</span>}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto' }}>
                    <span className="mk-card-price">
                      {((p.promo_price || p.price) || 0).toLocaleString('fr-FR')} FC
                      {p.promo_price ? <span className="mk-card-old"> {(p.price || 0).toLocaleString('fr-FR')}</span> : null}
                    </span>
                    <button
                      className="mk-iconbtn"
                      style={{ background: 'var(--mb-fire)', color: '#fff', width: 34, height: 34 }}
                      onClick={(e) => { e.stopPropagation(); quickAdd(p); }}
                      aria-label="Ajouter au panier"
                    >+</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {!visible.length && <div className="mk-center">Aucun produit ici pour le moment.</div>}
        </div>
      </div>

      {/* ── Fiche produit (options + quantité) ── */}
      {sel && (
        <div className="mk-overlay" onClick={() => setSel(null)}>
          <div className="mk-modal" onClick={e => e.stopPropagation()}>
            {(sel.image_url || sel.image) && <img className="mk-modal-img" src={sel.image_url || sel.image} alt="" />}
            <div className="mk-modal-body">
              <h3>{sel.name}</h3>
              {sel.description && <p className="mk-desc">{sel.description}</p>}
              <div className="mk-card-price" style={{ fontSize: '1.15rem' }}>
                {((sel.promo_price || sel.price) || 0).toLocaleString('fr-FR')} FC
                {sel.promo_price ? <span className="mk-card-old"> {(sel.price || 0).toLocaleString('fr-FR')} FC</span> : null}
              </div>

              <OptionsPicker
                groups={groupsFor(sel)}
                value={opts}
                onChange={setOpts}
                instructions={instr}
                onInstructions={setInstr}
              />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, gap: 12, flexWrap: 'wrap' }}>
                <div className="mk-qty">
                  <button onClick={() => setQty(v => Math.max(1, v - 1))}>−</button>
                  <b>{qty}</b>
                  <button onClick={() => setQty(v => (sel.max_qty_per_order && v >= sel.max_qty_per_order) ? v : v + 1)}>+</button>
                </div>
                <button className="mk-btn primary" style={{ flex: 1, minWidth: 220 }} onClick={() => tryAdd(sel, opts, instr, qty)}>
                  <ShoppingCart size={18} /> Ajouter — {(unitTotal * qty).toLocaleString('fr-FR')} FC
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Panier d'un autre commerce ── */}
      {confirmSwitch && (
        <div className="mk-overlay" onClick={() => setConfirmSwitch(null)}>
          <div className="mk-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="mk-modal-body">
              <h3>Changer de commerce ?</h3>
              <p className="mk-desc">Votre panier contient des articles de « {cart?.supplierName} ». L'ajouter depuis « {supplier.business_name} » videra le panier actuel.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="mk-btn ghost" style={{ flex: 1 }} onClick={() => setConfirmSwitch(null)}>Annuler</button>
                <button className="mk-btn primary" style={{ flex: 1 }} onClick={() => { const c = confirmSwitch; setConfirmSwitch(null); doAdd(c.p, c.options, c.instructions, c.quantity); }}>
                  Vider et ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
